import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { api } from '../api/client'
import ConnectorForm from '../components/ConnectorForm'
import type {
  ConnectorMapping,
  ConnectorProduct,
  MappingProposal,
  MappingSuggestion,
  RemoteObjectType,
  SyncEvent,
  SyncRun,
} from '../api/types'
import { PageSection, SectionedPage, Card, EmptyState, ErrorBanner, formatDateTime, Spinner } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Il flusso di un connettore, dal collegamento alla pianificazione.
 *
 * La pagina è organizzata nei passi che si percorrono, nell'ordine, e ogni passo
 * dice **cosa manca** per arrivare al successivo. La ragione non è estetica: i
 * vincoli veri stanno nel Control Plane — una mappatura non si esegue senza una
 * prova a vuoto, non si pianifica senza una prima sincronizzazione — e una
 * pagina che li nascondesse produrrebbe pulsanti che rispondono «409» senza
 * spiegare quale passo si è saltato.
 */
export default function ConnectorDetail() {
  const { id = '' } = useParams()
  const connector = useAsync(() => api.connector(id), [id])
  const mappings = useAsync(() => api.connectorMappings(id), [id])
  const runs = useAsync(() => api.connectorRuns(id), [id])
  // Una prova o un'esecuzione lanciata da qui: finché gira, lo storico si
  // ricarica da solo (la corsa è confermata subito e aggiorna i suoi
  // contatori), il menu la segnala e, alla fine, se ne apre il dettaglio.
  const [lanciata, setLanciata] = useState(false)
  const [apriRun, setApriRun] = useState<string | null>(null)
  const inCorso = lanciata || (runs.data ?? []).some((r) => r.status === 'running')
  useEffect(() => {
    if (!inCorso) return
    const timer = window.setInterval(() => runs.reload(), 3000)
    return () => window.clearInterval(timer)
  }, [inCorso]) // eslint-disable-line react-hooks/exhaustive-deps
  // Il catalogo dei prodotti serve al modulo di modifica: quali campi ha questo
  // connettore lo dichiara l'adattatore, non questa pagina.
  const products = useAsync(() => api.connectorCatalog(), [])

  const [catalog, setCatalog] = useState<RemoteObjectType[] | null>(null)
  const [busy, setBusy] = useState('')
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const objectTypes = catalog ?? []

  async function onTest() {
    setError(null)
    setBusy('test')
    try {
      await api.testConnector(id)
      connector.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy('')
    }
  }

  async function onScan() {
    setError(null)
    setBusy('scan')
    try {
      const result = await api.scanConnector(id)
      setCatalog(result.object_types)
      connector.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy('')
    }
  }

  if (connector.loading) return <Spinner />
  if (!connector.data) return <ErrorBanner error={connector.error ?? t('connettore inesistente')} />
  const c = connector.data
  const prodotto = (products.data?.products ?? []).find((p) => p.kind === c.kind)

  return (
    <>
      <div className="page__header">
        <h1 className="page__title">{c.name}</h1>
        <p className="page__subtitle">
          {c.product} — {c.base_url} · <Link to="/connectors">{t('tutti i connettori')}</Link>
        </p>
      </div>

      <ErrorBanner error={error} />

      <SectionedPage
        sections={[
          { id: 'collegamento', label: t('Collegamento') },
          { id: 'oggetti', label: t('Oggetti') },
          { id: 'mappature', label: t('Mappature') },
          { id: 'esecuzioni', label: inCorso ? `🔴 ${t('Esecuzioni')}` : t('Esecuzioni') },
        ]}
      >

      {/* 1 — Collegamento */}
      <PageSection id="collegamento">
      <Card
        title={t('1. Collegamento')}
        actions={
          <>
            <button className="secondary" onClick={() => setEditing((on) => !on)}>
              {editing ? t('Chiudi') : t('Modifica')}
            </button>{' '}
            <button onClick={onTest} disabled={busy !== ''}>
              {busy === 'test' ? t('Verifica…') : t('Prova il collegamento')}
            </button>
          </>
        }
      >
        <p className="text-muted" style={{ marginTop: 0 }}>
          {c.execution === 'probe' ? (
            <>
              {c.appliance_name
                ? t('Le chiamate escono da {sonda}.', { sonda: c.appliance_name })
                : t('Le chiamate escono da una sonda che non esiste più — riassegnala qui sotto, altrimenti non parte niente.')}
            </>
          ) : (
            <>{t('Le chiamate partono dal cloud.')}</>
          )}
        </p>

        {editing && (
          <div className="panel">
            <ConnectorForm
              product={prodotto}
              connector={c}
              onSaved={() => {
                setEditing(false)
                connector.reload()
              }}
            />
          </div>
        )}

        {c.last_test_ok === null ? (
          <EmptyState
            title={t('Mai provato')}
            hint={t("La prova dice anche chi risulti essere dall'altra parte: utenza e istanza. Un connettore puntato sull'ambiente di collaudo funziona benissimo, ed è proprio quello il problema.")}
          />
        ) : (
          <>
            <p>
              <span className={c.last_test_ok ? 'badge badge--ok' : 'badge badge--error'}>
                {c.last_test_ok ? t('riuscito') : t('fallito')}
              </span>{' '}
              <span className="text-muted">{formatDateTime(c.last_test_at)}</span>
            </p>
            <dl className="kv">
              {Object.entries(c.last_test_detail ?? {}).map(([key, value]) => (
                <Fragment key={key}>
                  <dt>{key}</dt>
                  <dd>{String(value)}</dd>
                </Fragment>
              ))}
            </dl>
          </>
        )}
      </Card>
      </PageSection>

      <PageSection id="oggetti">

      {/* 2 — Scansione */}
      <Card
        title={t('2. Cosa contiene il sistema di destinazione')}
        actions={
          <button onClick={onScan} disabled={busy !== '' || c.last_test_ok !== true}>
            {busy === 'scan' ? t('Scansione…') : t('Scandisci')}
          </button>
        }
      >
        {c.last_test_ok !== true ? (
          <p className="text-muted">{t('Prima serve un collegamento riuscito.')}</p>
        ) : !objectTypes.length && !c.scanned_at ? (
          <EmptyState
            title={t('Non ancora scandito')}
            hint={t('La scansione chiede al sistema quali tipi di oggetto esistono davvero su questa istanza.')}
          />
        ) : !objectTypes.length ? (
          <p className="text-muted">
            {t('Scansione del {quando}: {n} tipi di oggetto trovati. Rilanciala per rivederne l’elenco.', { quando: formatDateTime(c.scanned_at), n: c.object_types })}
          </p>
        ) : (
          <>
            {/* La spiegazione sta qui e non su ogni riga: su un'istanza vera i
                tipi sono più di cento, e la stessa frase ripetuta cento volte
                non informa — copre. Le righe portano solo ciò che le distingue. */}
            <p className="field__hint" style={{ marginTop: 0 }}>
              {prodotto?.separate_type
                ? t('I tipi di oggetto sono ciò che si crea e si aggiorna: un oggetto è composto da più definizioni di dato, e i suoi campi si indirizzano come {forma}.', { forma: 'Frammento.Attributo' })
                : t('I tipi di oggetto sono ciò che si crea e si aggiorna su questa istanza: scegline uno nella mappatura.')}
            </p>
            <ObjectTypeTable types={objectTypes} totale={c.object_types_total} />
            {prodotto?.separate_type && <Subtypes connectorId={id} types={objectTypes} />}
          </>
        )}
      </Card>
      </PageSection>

      <PageSection id="mappature">

      {/* 3 — Mappature */}
      <MappingsCard
        connectorId={id}
        prodotto={prodotto}
        mappings={mappings.data ?? []}
        loading={mappings.loading}
        objectTypes={objectTypes}
        scanned={Boolean(c.scanned_at)}
        onChanged={() => {
          mappings.reload()
          runs.reload()
          connector.reload()
        }}
        onStarted={() => {
          setLanciata(true)
          setApriRun(null)
          // Allo storico: la corsa si segue lì, col suo pallino.
          window.location.hash = 'esecuzioni'
          window.setTimeout(() => runs.reload(), 800)
        }}
        onFinished={(run) => {
          setLanciata(false)
          if (run) setApriRun(run.id)
          runs.reload()
        }}
      />

      {/* 4 — Storico */}
      </PageSection>

      <PageSection id="esecuzioni">
      <Card title={t('4. Cosa è successo')}>
        {inCorso && (
          <p className="banner banner--info">
            <span className="pallino-rosso" aria-hidden="true" />{' '}
            {t('Esecuzione in corso: i contatori si aggiornano da soli, e alla fine se ne apre il dettaglio.')}
          </p>
        )}
        {runs.loading && !runs.data ? <Spinner /> : <RunTable runs={runs.data ?? []} apri={apriRun} />}
      </Card>
      </PageSection>
      </SectionedPage>
    </>
  )
}

// ---------------------------------------------------------------- scansione

function ObjectTypeTable({ types, totale }: { types: RemoteObjectType[]; totale: number }) {
  const alimentati = types.filter((tipo) => tipo.supported).length
  const colonne: Column<RemoteObjectType>[] = [
    { key: 'key', header: t('Oggetto'), value: (tipo) => `${tipo.key} ${tipo.label}`.trim(), render: (tipo) => (
      <>
        <code>{tipo.key || '—'}</code>
        {tipo.supported && (
          <span className="badge badge--ok" title={t('mappatura di partenza pronta')}>
            {t('pronto')}
          </span>
        )}
        <div className="text-muted">{tipo.label}</div>
      </>
    ) },
    { key: 'category', header: t('Famiglia') },
    // «Non lo so» e «zero» portano a due decisioni opposte: si rinuncia a una
    // tabella vuota, si vanno a controllare i permessi quando il conteggio non
    // è arrivato. Mostrarli uguali fa prendere la prima al posto della seconda.
    { key: 'record_count', header: t('Record'), type: 'number', value: (tipo) => tipo.record_count, render: (tipo) => (
      tipo.record_count === null ? (
        <span className="text-muted">{t('non disponibile')}</span>
      ) : (
        tipo.record_count.toLocaleString(localeCorrente())
      )
    ) },
    { key: 'note', header: t('Nota'), render: (tipo) => <span className="text-muted">{tipo.note}</span> },
  ]
  return (
    <div>
      {/* Il catalogo di un'istanza Matrix42 ha trecento tipi. Di alcuni sappiamo
          due cose insieme — che una discovery produce quei dati e che esiste una
          mappatura di partenza verificata — e chi sceglie da un elenco di
          trecento voci non ha modo di saperlo. Sono in cima e sono marcati; gli
          altri ci sono tutti, perché mettere in evidenza non è nascondere. */}
      {alimentati > 0 && (
        <p className="field__hint" style={{ marginTop: 0 }}>
          {totale > types.length ? (
            <>
              {t('Di {totale} tipi dichiarati dal sistema sono elencati i {n} che CENSO sa riempire: per questi la mappatura arriva proposta e verificata. Gli altri esistono ancora — se ti serve un tipo personalizzato, scrivine il nome a mano nella mappatura.', { totale, n: types.length })}
            </>
          ) : (
            <>
              {t('I primi {n} sono i tipi che CENSO sa già riempire: per questi la mappatura arriva proposta e verificata.', { n: alimentati })}
            </>
          )}
        </p>
      )}
      <DataTable
        rows={types}
        columns={colonne}
        rowKey={(tipo) => tipo.key || tipo.label}
        conteggio={{ nome: t('tipi') }}
      />
    </div>
  )
}

/**
 * I tipi di oggetto che usano un frammento, chiesti all'istanza.
 *
 * Serve per un caso preciso: in Matrix42 un asset non è una riga, è un **oggetto
 * di un tipo** composto da più frammenti — e `SPSAssetClassBase`, il frammento
 * comune a tutti gli asset, da solo non si crea. Per crearne uno bisogna sapere
 * il tipo, e il tipo dipende dall'installazione.
 *
 * Si legge dai dati e non da un elenco nel prodotto: una tendina scritta a mano
 * sarebbe giusta per la prima istanza e sbagliata per la seconda, in silenzio.
 */
function Subtypes({ connectorId, types }: { connectorId: string; types: RemoteObjectType[] }) {
  const [aperto, setAperto] = useState<string | null>(null)
  const trovati = useAsync(
    () => (aperto ? api.objectSubtypes(connectorId, aperto) : Promise.resolve(null)),
    [connectorId, aperto],
  )

  const candidati = types.filter((tipo) => tipo.key)
  if (!candidati.length) return null

  return (
    <div style={{ marginTop: 16 }}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Per creare in Matrix42 serve il tipo di oggetto, non il frammento: un asset è un oggetto di un tipo, composto da più frammenti. Qui sotto i tipi trovati su questa istanza, da incollare nella configurazione del connettore.')}
      </p>
      <div className="rowactions" style={{ textAlign: 'left' }}>
        {candidati.slice(0, 8).map((tipo) => (
          <button
            key={tipo.key}
            className="secondary"
            onClick={() => setAperto(aperto === tipo.key ? null : tipo.key)}
          >
            {aperto === tipo.key ? t('Nascondi') : t('Tipi di {tipo}', { tipo: tipo.key })}
          </button>
        ))}
      </div>

      {trovati.loading && <Spinner />}
      <ErrorBanner error={trovati.error} />
      {trovati.data && !trovati.data.supported && (
        <p className="text-muted">{trovati.data.note}</p>
      )}
      {trovati.data?.supported && trovati.data.types.length === 0 && (
        <p className="text-muted">{trovati.data.note}</p>
      )}
      {trovati.data?.supported && trovati.data.types.length > 0 && (
        <>
          <ul className="list">
            {trovati.data.types.map((s) => (
              <li key={s.key}>
                <code>{s.key}</code>{' '}
                {/* Il nome leggibile accanto a quello tecnico: uno serve a
                    funzionare, l'altro a riconoscere quale sia il proprio. */}
                {s.label ? <span className="text-muted">({s.label})</span> : null}{' '}
                {/* Dallo schema il conteggio non c'è: scriverne uno — anche
                    «0» — direbbe una cosa che non sappiamo, e «0» direbbe
                    proprio quella sbagliata, cioè «tipo esistente e vuoto». */}
                {s.count === null ? null : (
                  <span className="text-muted">
                    — {t('{n} su {campione} letti', { n: s.count, campione: trovati.data?.sampled })}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {/* Un campione, non un censimento: senza dirlo il numero si legge come
              «quanti asset ci sono di quel tipo», che su un parco grande è falso
              di un ordine di grandezza. */}
          {trovati.data.source === 'schema' ? (
            <p className="field__hint">{trovati.data.note}</p>
          ) : (
            <p className="field__hint">
              {trovati.data.truncated
                ? t('Letti da {colonna} su un campione di {n} record (ce ne sono altri): l’elenco dice quali tipi esistono, non quanti oggetti hanno.', { colonna: trovati.data.column, n: trovati.data.sampled })
                : t('Letti da {colonna} su un campione di {n} record: l’elenco dice quali tipi esistono, non quanti oggetti hanno.', { colonna: trovati.data.column, n: trovati.data.sampled })}
            </p>
          )}
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- mappature

function MappingsCard({
  connectorId,
  prodotto,
  mappings,
  loading,
  objectTypes,
  scanned,
  onChanged,
  onStarted,
  onFinished,
}: {
  connectorId: string
  prodotto?: ConnectorProduct
  /** Una prova o un'esecuzione parte: il genitore porta allo storico. */
  onStarted?: () => void
  /** È finita (con la corsa, se è arrivata). */
  onFinished?: (run: SyncRun | null) => void
  mappings: ConnectorMapping[]
  loading: boolean
  objectTypes: RemoteObjectType[]
  scanned: boolean
  onChanged: () => void
}) {
  const [composing, setComposing] = useState(false)
  // Il connettore ITSM si vende a parte: senza il modulo in licenza si
  // configura e si prova a vuoto — la prova è ciò che lo fa comprare — ma non
  // si sincronizza né si pianifica. Lo diciamo sul pulsante, prima del clic,
  // invece di lasciarlo scoprire da un errore del server.
  const licenza = useAsync(() => api.license(), [])
  const senzaModulo = licenza.data != null && licenza.data.features?.itsm_connector !== true
  // La mappatura aperta in modifica. Prima si poteva solo cancellare e
  // rifare: su una mappatura con dieci coppie, cambiarne una costava
  // riscriverle tutte — e ogni riscrittura è un'occasione di sbagliarne una
  // che prima era giusta.
  const [modifica, setModifica] = useState<ConnectorMapping | null>(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [lastRun, setLastRun] = useState<SyncRun | null>(null)

  async function act(action: string, mapping: ConnectorMapping) {
    if (action === 'delete' && !window.confirm(t('Eliminare questa mappatura del connettore? La sincronizzazione si ferma. Non si può annullare.'))) return
    setError(null)
    setBusy(mapping.id + action)
    const corsa = action === 'dry' || action === 'run'
    if (corsa) onStarted?.()
    let finita: SyncRun | null = null
    try {
      if (action === 'dry') setLastRun((finita = await api.dryRunMapping(connectorId, mapping.id)))
      else if (action === 'run') setLastRun((finita = await api.runMapping(connectorId, mapping.id)))
      else if (action === 'schedule')
        await api.scheduleMapping(connectorId, mapping.id, !mapping.enabled)
      else if (action === 'delete') await api.deleteMapping(connectorId, mapping.id)
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy('')
      if (corsa) onFinished?.(finita)
    }
  }

  const colonne: Column<ConnectorMapping>[] = [
    { key: 'name', header: t('Mappatura'), value: (m) => `${m.name} ${m.source_entity} ${m.target_object}`, render: (m) => (
      <>
        <strong>{m.name}</strong>
        <div className="text-muted">
          {m.source_entity} → <code>{m.target_object}</code> · {t('chiave')}{' '}
          <code>
            {m.match_source} = {m.match_target}
          </code>{' '}
          · {t('{n} campi', { n: m.field_map.length })}
        </div>
      </>
    ) },
    { key: 'direction', header: t('Direzione'), value: (m) => DIRECTION_LABEL[m.direction]?.() ?? m.direction },
    { key: 'recurrence', header: t('Frequenza') },
    { key: 'state', header: t('Stato'), value: (m) => (m.paused_reason ? t('sospesa') : m.enabled ? t('pianificata') : t('non pianificata')), render: (m) => <MappingState mapping={m} /> },
    { key: 'last_run_at', header: t('Ultima esecuzione'), type: 'date', value: (m) => m.last_run_at ?? '', render: (m) => (
      m.last_run_at ? (
        <>
          <span className={m.last_status === 'failed' ? 'badge badge--error' : 'badge badge--ok'}>
            {m.last_status}
          </span>{' '}
          <span className="text-muted">{formatDateTime(m.last_run_at)}</span>
          {m.last_error && <div className="text-muted">{m.last_error}</div>}
        </>
      ) : (
        <span className="text-muted">{t('mai')}</span>
      )
    ) },
    { key: 'azioni', header: t('Azioni'), sort: false, filter: false, searchable: false, render: (m) => (
      <div className="rowactions">
        <button
          className="secondary"
          onClick={() => {
            setComposing(false)
            setModifica((aperta) => (aperta?.id === m.id ? null : m))
          }}
        >
          {modifica?.id === m.id ? t('Chiudi') : t('Modifica')}
        </button>
        <button className="secondary" onClick={() => act('dry', m)} disabled={busy !== ''}>
          {t('Prova a vuoto')}
        </button>
        <button
          onClick={() => act('run', m)}
          disabled={busy !== '' || m.last_dry_run_at === null || senzaModulo}
          title={
            senzaModulo
              ? t('La sincronizzazione verso l’ITSM richiede il modulo «ITSM Connector», che si aggiunge a qualunque piano. La prova a vuoto resta disponibile.')
              : m.last_dry_run_at === null
                ? t('Fai prima una prova a vuoto: mostra cosa verrebbe scritto, e nessuna scrittura si annulla')
                : undefined
          }
        >
          {t('Sincronizza')}
        </button>
        <button
          className="secondary"
          onClick={() => act('schedule', m)}
          disabled={busy !== '' || (!m.enabled && (!m.schedulable || senzaModulo))}
          title={
            !m.enabled && senzaModulo
              ? t('La sincronizzazione verso l’ITSM richiede il modulo «ITSM Connector», che si aggiunge a qualunque piano. La prova a vuoto resta disponibile.')
              : !m.enabled && !m.schedulable
                ? m.schedulable_reason
                : undefined
          }
        >
          {m.enabled ? t('Sospendi') : t('Pianifica')}
        </button>
        <button className="danger" onClick={() => act('delete', m)} disabled={busy !== ''}>
          {t('Elimina')}
        </button>
      </div>
    ) },
  ]

  return (
    <Card
      title={t('3. Mappature, direzione e frequenza')}
      actions={
        <button
          onClick={() => {
            setModifica(null)
            setComposing((v) => !v)
          }}
          disabled={!scanned}
        >
          {composing ? t('Chiudi') : t('Nuova mappatura')}
        </button>
      }
    >
      {!scanned && (
        <p className="text-muted">
          {t('Prima serve una scansione: il mapping si costruisce sui campi che il sistema di destinazione ha davvero, non su un elenco teorico.')}
        </p>
      )}

      <ErrorBanner error={error} />

      {(composing || modifica) && scanned && (
        <MappingComposer
          key={modifica?.id ?? 'nuova'}
          connectorId={connectorId}
          prodotto={prodotto}
          objectTypes={objectTypes}
          mapping={modifica}
          onSaved={() => {
            setComposing(false)
            setModifica(null)
            onChanged()
          }}
        />
      )}

      {senzaModulo && <p className="field__hint">{t('La sincronizzazione verso l’ITSM richiede il modulo «ITSM Connector», che si aggiunge a qualunque piano. La prova a vuoto resta disponibile.')}</p>}

      {loading ? (
        <Spinner />
      ) : !mappings.length ? (
        <EmptyState title={t('Nessuna mappatura')} hint={t('Una mappatura dice cosa sincronizzare, in quale direzione e ogni quanto.')} />
      ) : (
        <DataTable
          rows={mappings}
          columns={colonne}
          rowKey={(m) => m.id}
          conteggio={{ nome: t('mappature') }}
        />
      )}

      {lastRun && <RunDetail run={lastRun} />}
    </Card>
  )
}

// Funzioni e non stringhe: `t()` va chiamata quando si disegna.
const DIRECTION_LABEL: Record<string, () => string> = {
  push: () => t('CENSO → destinazione'),
  pull: () => t('destinazione → CENSO'),
  both: () => t('doppio senso'),
}

/**
 * A che punto è la mappatura nel flusso, e cosa manca.
 *
 * Tre campi separati — prova a vuoto, prima sincronizzazione, pianificazione —
 * costringerebbero chi guarda a ricomporli, e a ricomporli male: «pianificata»
 * accesa su una mappatura mai eseguita non esiste, ma per saperlo bisogna
 * confrontare due colonne.
 */
function MappingState({ mapping }: { mapping: ConnectorMapping }) {
  if (mapping.paused_reason) {
    return (
      <>
        <span className="badge badge--warn">{t('sospesa')}</span>
        <div className="text-muted">{mapping.paused_reason}</div>
      </>
    )
  }
  if (mapping.enabled) {
    return (
      <>
        <span className="badge badge--ok">{t('pianificata')}</span>
        <div className="text-muted">{t('prossima: {quando}', { quando: formatDateTime(mapping.next_run_at) })}</div>
      </>
    )
  }
  return (
    <>
      <span className="badge badge--muted">{t('non pianificata')}</span>
      {mapping.schedulable_reason && <div className="text-muted">{mapping.schedulable_reason}</div>}
    </>
  )
}

// ------------------------------------------------------- composizione mapping

/** Il testo della destinazione automatica di un prodotto. Le frasi note sono
 *  qui per la traduzione (la guardia legge solo i `t()` del sorgente); un
 *  prodotto nuovo mostra l'etichetta del catalogo così com'è. */
function etichettaAutomatica(chiave: string, dalCatalogo?: string): string {
  const note: Record<string, string> = {
    censo: t('Schema gestito da CENSO (automatico): il tipo di oggetto lo decide la classe di ogni CI'),
    'censo:cmdb': t('CMDB per classe (automatico): ogni CI nella tabella standard della sua classe'),
    'censo:moduli': t('Moduli per classe (automatico): i computer in Workstations, gli apparati negli Asset'),
  }
  return note[chiave] ?? dalCatalogo ?? chiave
}

function MappingComposer({
  connectorId,
  prodotto,
  objectTypes,
  mapping,
  onSaved,
}: {
  connectorId: string
  /** Il prodotto del connettore: decide se serve il campo «tipo dell'oggetto». */
  prodotto?: ConnectorProduct

  objectTypes: RemoteObjectType[]
  /** La mappatura da modificare. `null` per comporne una nuova. */
  mapping?: ConnectorMapping | null
  onSaved: () => void
}) {
  const catalog = useAsync(() => api.connectorCatalog(), [])
  const entities = catalog.data?.source_entities ?? []

  // Su una mappatura nuova si parte da dove i dati **ci sono**. Prima si partiva
  // sempre da «Asset», che su un cliente che sincronizza la discovery è vuoto: il
  // compositore si apriva su un'entità senza record, fra i campi non c'era niente
  // che parlasse di macchine virtuali — sono Configuration Item — e la
  // conclusione ragionevole era che il connettore non le sapesse trattare.
  const piuPopolata =
    [...entities].sort((a, b) => (b.records ?? 0) - (a.records ?? 0))[0]?.key ?? 'asset'
  const [sourceEntity, setSourceEntity] = useState(
    mapping?.source_entity ?? piuPopolata,
  )
  // I valori dei filtri vengono dai **dati di questo cliente**, non da un elenco
  // nel prodotto: chi sceglie deve sapere che «console server» esiste e che ne
  // ha quattro, non che potrebbe esistere.
  const dettaglio = useAsync(() => api.sourceEntity(sourceEntity), [sourceEntity])
  const [sourceFilter, setSourceFilter] = useState<Record<string, string>>(
    Object.fromEntries(
      Object.entries(mapping?.source_filter ?? {}).map(([k, v]) => [k, String(v)]),
    ),
  )
  // Digitabile e non solo selezionabile: su Remedy e Matrix42 personalizzati i
  // nomi dei moduli sono diversi da qualunque elenco, e la scansione lo dice.
  // Dove il prodotto sa scegliere da sé la destinazione (Jira: lo schema
  // gestito da CENSO), quella è la scelta di partenza; resta sempre possibile
  // scegliere un tipo a mano per una mappatura personalizzata.
  const automatica = prodotto?.auto_target ?? ''
  const [modoDestinazione, setModoDestinazione] = useState<'auto' | 'manuale'>(
    automatica && (!mapping || mapping.target_object === automatica) ? 'auto' : 'manuale',
  )
  const [targetObject, setTargetObject] = useState(
    mapping?.target_object ?? (automatica ? automatica : ''),
  )
  // Il catalogo dei prodotti può arrivare dopo l'apertura: su una mappatura
  // nuova e ancora vuota si passa alla destinazione automatica appena la si sa.
  useEffect(() => {
    if (automatica && !mapping && !targetObject) {
      setModoDestinazione('auto')
      setTargetObject(automatica)
    }
  }, [automatica]) // eslint-disable-line react-hooks/exhaustive-deps
  const [targetType, setTargetType] = useState(mapping?.target_type ?? '')
  const [name, setName] = useState(mapping?.name ?? '')
  const [direction, setDirection] = useState(mapping?.direction ?? 'push')
  const [conflictPolicy, setConflictPolicy] = useState(mapping?.conflict_policy ?? 'adabs_wins')
  const [cron, setCron] = useState(mapping?.cron_expression ?? '0 2 * * *')
  const [createMissing, setCreateMissing] = useState(mapping?.create_missing ?? true)
  const [writeCollections, setWriteCollections] = useState(mapping?.write_collections ?? false)
  const [updateExisting, setUpdateExisting] = useState(mapping?.update_existing ?? true)

  const [proposal, setProposal] = useState<MappingProposal | null>(null)
  const [pairs, setPairs] = useState<MappingSuggestion[]>(
    // Le coppie già salvate: modificarne una non deve costare riscriverle tutte.
    (mapping?.field_map ?? []).map((p) => ({
      source: p.source,
      target: p.target,
      score: 1,
      reason: t('già nella mappatura'),
      warning: '',
    })),
  )
  const [matchSource, setMatchSource] = useState(mapping?.match_source ?? '')
  // La coppia che si sta componendo a mano. Vedi il modulo qui sotto: senza,
  // «il mapping va costruito a mano» era una frase senza niente per farlo.
  const [nuovaSorgente, setNuovaSorgente] = useState('')
  const [nuovaDestinazione, setNuovaDestinazione] = useState('')
  const [matchTarget, setMatchTarget] = useState(mapping?.match_target ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  // In modifica la proposta non c'è ancora: i campi arrivano dalla scansione e
  // dal dettaglio dell'entità, che è ciò che si ha prima di premere «Proponi».
  // Senza questo ripiego, aprire una mappatura per cambiarne una riga
  // presenterebbe tendine vuote — cioè costringerebbe di nuovo a rifarla.
  const campiSorgente = proposal?.source_entity.fields ?? dettaglio.data?.fields ?? []
  const campiDestinazione =
    proposal?.target_object.fields ?? objectTypes.find((tipo) => tipo.key === targetObject)?.fields ?? []

  const chosen = useMemo(() => objectTypes.find((tipo) => tipo.key === targetObject), [
    objectTypes,
    targetObject,
  ])
  // Solo quando il nome scritto corrisponde a un oggetto scandito: la
  // dipendenza cambia due volte mentre si digita, non a ogni tasto.
  const tipoSeparato = Boolean(prodotto?.separate_type)
  const tipiOggetto = useAsync(
    () =>
      tipoSeparato && chosen?.key ? api.objectSubtypes(connectorId, chosen.key) : Promise.resolve(null),
    [connectorId, chosen?.key, tipoSeparato],
  )

  async function onSuggest() {
    setError(null)
    setBusy(true)
    try {
      const result = await api.suggestMapping(connectorId, sourceEntity, targetObject)
      setProposal(result)
      setPairs(result.field_map)
      setMatchSource(result.match_source)
      setMatchTarget(result.match_target)
      if (!name) setName(`${sourceEntity} → ${result.target_object.label}`)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function onSave() {
    setError(null)
    setBusy(true)
    try {
      const corpo = {
        name: name.trim(),
        source_entity: sourceEntity,
        // Solo le voci scelte: una chiave con valore vuoto sarebbe un filtro
        // dichiarato e inerte, cioè la cosa che questo campo esiste per evitare.
        source_filter: Object.fromEntries(
          Object.entries(sourceFilter).filter(([, v]) => v !== ''),
        ),
        target_object: targetObject,
        target_type: targetType.trim(),
        field_map: pairs.map((p) => ({ source: p.source, target: p.target })),
        match_source: matchSource,
        match_target: matchTarget,
        direction,
        conflict_policy: conflictPolicy,
        create_missing: createMissing,
        write_collections: writeCollections,
        update_existing: updateExisting,
        cron_expression: cron.trim() || null,
      }
      if (mapping) await api.updateMapping(connectorId, mapping.id, corpo)
      else await api.createMapping(connectorId, corpo)
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card__body--flush">
      <div className="row">
        <label className="field">
          <span>{t('Cosa sincronizzare')}</span>
          <select value={sourceEntity} onChange={(e) => setSourceEntity(e.target.value)}>
            {entities.map((e) => (
              <option key={e.key} value={e.key}>
                {e.label}
                {e.records === undefined ? '' : ` — ${t('{n} record', { n: e.records.toLocaleString(localeCorrente()) })}`}
              </option>
            ))}
          </select>
          <span className="field__hint">
            {entities.find((e) => e.key === sourceEntity)?.description}
          </span>
        </label>

        {/* Restringere la sorgente è ciò che permette a due mappature di andare
            a finire in due tipi diversi dello stesso sistema: gli apparati di
            rete in un tipo, i computer in un altro. Senza, o si manda tutto a
            uno o si manda tutto all'altro. */}
        {(dettaglio.data?.filters ?? []).map((f) => (
          <label className="field" key={f.field}>
            <span>{t('Solo {cosa}', { cosa: f.label.toLowerCase() })}</span>
            <select
              value={sourceFilter[f.field] ?? ''}
              onChange={(e) =>
                setSourceFilter((prima) => ({ ...prima, [f.field]: e.target.value }))
              }
            >
              <option value="">{t('tutti')}</option>
              {f.values.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.value} ({v.count})
                </option>
              ))}
            </select>
            <span className="field__hint">
              {t('Il numero è quanti ne ha questo cliente adesso: è ciò che dice se il filtro sta per prendere l’insieme che hai in mente o un decimo.')}
            </span>
          </label>
        ))}

        {automatica && (
          <div className="field">
            <span>{t('Destinazione')}</span>
            <label>
              <input
                type="radio"
                checked={modoDestinazione === 'auto'}
                onChange={() => {
                  setModoDestinazione('auto')
                  setTargetObject(automatica)
                }}
              />{' '}
              {etichettaAutomatica(automatica, prodotto?.auto_target_label)}
            </label>
            <label>
              <input
                type="radio"
                checked={modoDestinazione === 'manuale'}
                onChange={() => {
                  setModoDestinazione('manuale')
                  setTargetObject('')
                }}
              />{' '}
              {t('Un tipo di oggetto a scelta (mappatura personalizzata)')}
            </label>
          </div>
        )}
        {modoDestinazione === 'manuale' && (
        <label className="field">
          <span>{t('Verso quale oggetto')}</span>
          <input
            list="connector-object-types"
            value={targetObject}
            onChange={(e) => setTargetObject(e.target.value)}
            placeholder={t('scegli o scrivi il nome')}
          />
          <datalist id="connector-object-types">
            {objectTypes
              .filter((tipo) => tipo.key)
              .map((tipo) => (
                <option key={tipo.key} value={tipo.key}>
                  {tipo.label}
                </option>
              ))}
          </datalist>
          <span className="field__hint">
            {chosen?.note ??
              t('Se il tipo che cerchi non è nell’elenco, scrivine il nome: su installazioni personalizzate è la norma.')}
          </span>
        </label>
        )}

        {/* Contenitore e tipo sono due cose diverse solo su alcuni prodotti, ed
            è il motivo per cui questo campo è separato e facoltativo. In
            Matrix42 `SPSAssetClassBase` è il frammento su cui si scrive, uguale
            per tutti gli asset; il tipo decide dove il cliente andrà a trovare
            l'oggetto — fra i computer o fra gli apparati di rete. Sta qui sulla
            mappatura e non sul connettore perché un solo Matrix42 li riceve
            entrambi. */}
        {tipoSeparato && (
        <label className="field">
          <span>{t('Tipo dell’oggetto da creare')}</span>
          <input
            list="mapping-object-types"
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
            placeholder={t('solo dove il prodotto lo distingue')}
          />
          {/* Gli stessi tipi del pulsante «Tipi di …», qui dove si usano: farli
              leggere là e ricopiare qui è un passaggio a mano che si può
              sbagliare, su un valore che decide dove finiscono gli oggetti. */}
          <datalist id="mapping-object-types">
            {(tipiOggetto.data?.types ?? []).map((tipo) => (
              <option key={tipo.key} value={tipo.key}>
                {tipo.label || (tipo.count === null ? '' : t('{n} oggetti', { n: tipo.count }))}
              </option>
            ))}
          </datalist>
          <span className="field__hint">
            {t('Su {prodotto} il contenitore su cui si scrive e il tipo dell’oggetto che nasce sono due cose diverse: scegli qui il tipo. Il pulsante «Tipi di …» nella scansione qui sopra elenca quelli di questa istanza.', { prodotto: prodotto?.product ?? '' })}
          </span>
        </label>
        )}

        <div className="field">
          <span>&nbsp;</span>
          <button onClick={onSuggest} disabled={busy || !targetObject}>
            {busy ? t('Analisi…') : t('Proponi il mapping')}
          </button>
        </div>
      </div>

      <ErrorBanner error={error} />

      {(proposal || mapping) && (
        <>
          <SuggestionTable
            pairs={pairs}
            onRemove={(target) => setPairs(pairs.filter((p) => p.target !== target))}
          />

          {/* Aggiungere una coppia a mano non è un ripiego: è il caso normale su
              un'installazione personalizzata, dove i nomi dei campi non somigliano
              a niente. Senza questo modulo, un elenco di proposte vuoto lasciava
              senza nessuna azione possibile — con scritto sotto che il mapping
              andava costruito a mano. */}
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <label className="field">
              <span>{t('Campo CENSO')}</span>
              <input
                list="mapping-source-fields"
                value={nuovaSorgente}
                onChange={(e) => setNuovaSorgente(e.target.value)}
                placeholder={t('scegli o scrivi')}
              />
              <datalist id="mapping-source-fields">
                {campiSorgente.map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.label}
                  </option>
                ))}
              </datalist>
            </label>
            <label className="field">
              <span>{t('Campo di destinazione')}</span>
              <input
                list="mapping-target-fields"
                value={nuovaDestinazione}
                onChange={(e) => setNuovaDestinazione(e.target.value)}
                placeholder={t('scegli o scrivi')}
              />
              {/* Digitabile e non solo selezionabile: quando l'istanza non
                  restituisce lo schema di una definizione, l'elenco è vuoto e
                  l'unico modo di mappare è scrivere il nome del campo. */}
              <datalist id="mapping-target-fields">
                {campiDestinazione.map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.label}
                  </option>
                ))}
              </datalist>
            </label>
            <div className="field">
              <span>&nbsp;</span>
              <button
                className="secondary"
                disabled={!nuovaSorgente.trim() || !nuovaDestinazione.trim()}
                onClick={() => {
                  const source = nuovaSorgente.trim()
                  const target = nuovaDestinazione.trim()
                  // Due campi CENSO sullo stesso campo remoto darebbero un
                  // valore che dipende dall'ordine: il Control Plane lo rifiuta,
                  // e qui si sostituisce invece di far salvare per poi fallire.
                  setPairs([
                    ...pairs.filter((x) => x.target !== target),
                    { source, target, score: 1, reason: t('scelto a mano'), warning: '' },
                  ])
                  setNuovaSorgente('')
                  setNuovaDestinazione('')
                }}
              >
                {t('Aggiungi la coppia')}
              </button>
            </div>
          </div>

          <div className="row">
            <label className="field">
              <span>{t('Nome della mappatura')}</span>
              <input value={name} onChange={(e) => setName(e.target.value)} required />
            </label>
            <label className="field">
              <span>{t('Chiave CENSO')}</span>
              <select value={matchSource} onChange={(e) => setMatchSource(e.target.value)}>
                <option value="">—</option>
                {campiSorgente.map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.label} ({f.name})
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>{t('Chiave di destinazione')}</span>
              {/* Scrivibile, e non solo selezionabile: l'elenco dei campi arriva
                  dal sistema di destinazione, e quando quello non li dichiara —
                  succede — una tendina chiusa impedisce di indicare la chiave,
                  cioè impedisce di fare la mappatura. I campi già accostati
                  compaiono comunque, perché la chiave è quasi sempre uno di
                  quelli. */}
              <input
                list="mapping-key-targets"
                value={matchTarget}
                onChange={(e) => setMatchTarget(e.target.value)}
                placeholder={t('scegli o scrivi')}
              />
              <datalist id="mapping-key-targets">
                {[
                  ...new Set([
                    ...pairs.map((p) => p.target),
                    ...campiDestinazione.map((f) => f.name),
                  ]),
                ].map((nome) => (
                  <option key={nome} value={nome} />
                ))}
              </datalist>
              <span className="field__hint">
                {t('È il campo con cui si riconosce «lo stesso oggetto». Sbagliarlo non dà un errore: dà un doppione a ogni esecuzione, e il conteggio resta plausibile.')}
              </span>
            </label>
          </div>

          {/* Il motivo della chiave proposta, scritto. Sbagliarla non produce un
              errore: produce duplicati a ogni esecuzione, e il conteggio resta
              plausibile — quindi la scelta va capita, non accettata. */}
          {proposal?.match_reason && (
            <p className="banner banner--info">{t('Chiave proposta: {motivo}', { motivo: proposal.match_reason })}</p>
          )}

          {/* I campi lasciati fuori di proposito, con il motivo. Senza, un campo
              che manca dalle proposte sembra dimenticato: chi lo cerca lo mappa a
              mano nel primo campo remoto che gli somiglia nel nome, che è quasi
              sempre il posto sbagliato — «attributes.disk» finiva su
              «FloppyDisk», un booleano sul lettore di floppy. */}
          {proposal?.excluded?.length ? (
            <details className="banner banner--info">
              <summary>
                {t('{n} campi non proposti, di proposito — vedi perché', { n: proposal.excluded.length })}
              </summary>
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                {proposal.excluded.map((e) => (
                  <li key={e.source}>
                    <code>{e.source}</code>: {e.reason}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          <div className="row">
            <label className="field">
              <span>{t('Direzione')}</span>
              <select value={direction} onChange={(e) => setDirection(e.target.value)}>
                {(catalog.data?.directions ?? []).map((d) => (
                  <option key={d} value={d}>
                    {DIRECTION_LABEL[d]?.() ?? d}
                  </option>
                ))}
              </select>
            </label>
            {direction === 'both' && (
              <label className="field">
                <span>{t('Chi vince sui campi senza direzione')}</span>
                <select
                  value={conflictPolicy}
                  onChange={(e) => setConflictPolicy(e.target.value)}
                >
                  {(catalog.data?.conflict_policies ?? []).map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="field">
              <span>{t('Frequenza (cron)')}</span>
              <input value={cron} onChange={(e) => setCron(e.target.value)} placeholder="0 2 * * *" />
              <span className="field__hint">
                {t('Vale solo dopo la prima sincronizzazione riuscita: la pianificazione si accende in un secondo momento.')}
              </span>
            </label>
          </div>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={createMissing}
              onChange={(e) => setCreateMissing(e.target.checked)}
            />
            <span>{t('Crea gli oggetti che nella destinazione non ci sono')}</span>
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={updateExisting}
              onChange={(e) => setUpdateExisting(e.target.checked)}
            />
            <span>{t('Aggiorna quelli che ci sono già')}</span>
          </label>
          {/* Spenta di proposito, e con il motivo accanto. Non è un campo in
              più: su Matrix42 il software installato alimenta il riconoscimento
              dei prodotti e il calcolo delle licenze, e un inventario
              approssimativo diventa un numero su cui qualcuno firma un ordine.
              Chi la accende deve saperlo. */}
          <label className="checkbox">
            <input
              type="checkbox"
              checked={writeCollections}
              onChange={(e) => setWriteCollections(e.target.checked)}
            />
            <span>{t('Scrivi anche il software installato')}</span>
          </label>
          {writeCollections && (
            <p className="banner banner--info">
              {t('Sul sistema di destinazione il software installato alimenta il riconoscimento dei prodotti e il fabbisogno di licenze: accendilo solo se l’inventario software di questo parco è completo. Due cose da sapere: le righe si scrivono alla creazione dell’oggetto — su uno che esiste già si possono aggiornare e togliere, non aggiungere — e ciò che non si riesce ad aggiungere compare nel rapporto della corsa invece di essere taciuto.')}
            </p>
          )}

          <button onClick={onSave} disabled={busy || !name.trim()}>
            {mapping ? t('Salva le modifiche') : t('Salva la mappatura')}
          </button>
          <p className="text-muted">
            {mapping
              ? t('Cambiare i campi, la chiave o l’oggetto azzera la prova a vuoto e la pianificazione: la prova è ciò che autorizza a scrivere, e vale per la mappatura che è stata provata — non per quella che diventa dopo.')
              : t('Nasce spenta: il passo successivo è la prova a vuoto, che calcola tutto e non scrive niente.')}
          </p>
        </>
      )}
    </div>
  )
}

/** Le coppie proposte, con punteggio, motivo e ciò che può andare storto. */
function SuggestionTable({
  pairs,
  onRemove,
}: {
  pairs: MappingSuggestion[]
  onRemove: (target: string) => void
}) {
  if (!pairs.length) {
    return (
      <EmptyState
        title={t('Nessun accostamento proposto')}
        hint={t('I nomi dei campi non si somigliano abbastanza. Non è un guasto: significa che il mapping va costruito a mano.')}
      />
    )
  }
  const colonne: Column<MappingSuggestion>[] = [
    { key: 'source', header: t('Campo CENSO'), render: (p) => <code>{p.source}</code> },
    { key: 'target', header: t('Campo di destinazione'), render: (p) => <code>{p.target}</code> },
    { key: 'reason', header: t('Perché'), value: (p) => `${p.reason} (${Math.round(p.score * 100)}%)`, render: (p) => (
      <>
        {p.reason}{' '}
        <span className="text-muted">({Math.round(p.score * 100)}%)</span>
      </>
    ) },
    { key: 'warning', header: t('Da guardare'), render: (p) => <span className="text-muted">{p.warning}</span> },
    { key: 'azioni', header: '', sort: false, filter: false, searchable: false, render: (p) => (
      <button className="link" onClick={() => onRemove(p.target)}>
        {t('togli')}
      </button>
    ) },
  ]
  // Every pair is shown: all of them are written with the mapping.
  return <DataTable rows={pairs} columns={colonne} rowKey={(p) => p.target} paginazione={false} />
}

// ------------------------------------------------------------------ storico

function RunTable({ runs, apri }: { runs: SyncRun[]; apri?: string | null }) {
  const [open, setOpen] = useState<string | null>(null)
  // A fine corsa il genitore chiede di aprire il dettaglio di quella appena finita.
  useEffect(() => {
    if (apri) setOpen(apri)
  }, [apri])
  if (!runs.length) {
    return <EmptyState title={t('Nessuna esecuzione')} hint={t('Prove a vuoto comprese.')} />
  }
  const colonne: Column<SyncRun>[] = [
    { key: 'started_at', header: t('Quando'), type: 'date', value: (r) => r.started_at ?? '', render: (r) => formatDateTime(r.started_at) },
    { key: 'trigger', header: t('Tipo'), value: (r) => (r.dry_run ? t('prova a vuoto') : r.trigger), render: (r) => (
      r.dry_run ? (
        <span className="badge badge--info">{t('prova a vuoto')}</span>
      ) : (
        <span className="badge badge--muted">{r.trigger}</span>
      )
    ) },
    { key: 'status', header: t('Esito'), value: (r) => (r.status === 'running' ? t('in corso') : r.status), render: (r) => (
      r.status === 'running' ? (
        <span className="badge badge--info">
          <span className="pallino-rosso" aria-hidden="true" /> {t('in corso')}
        </span>
      ) : (
        <span
          className={
            r.status === 'failed'
              ? 'badge badge--error'
              : r.status === 'partial'
                ? 'badge badge--warn'
                : 'badge badge--ok'
          }
        >
          {r.status}
        </span>
      )
    ) },
    { key: 'examined', header: t('Esaminati'), type: 'number' },
    { key: 'created', header: t('Creati'), type: 'number' },
    { key: 'updated', header: t('Aggiornati'), type: 'number' },
    { key: 'unchanged', header: t('Invariati'), type: 'number' },
    { key: 'skipped', header: t('Saltati'), type: 'number' },
    { key: 'failed', header: t('Falliti'), type: 'number' },
    { key: 'azioni', header: '', sort: false, filter: false, searchable: false, render: (r) => (
      <button className="link" onClick={() => setOpen(open === r.id ? null : r.id)}>
        {open === r.id ? t('chiudi') : t('dettaglio')}
      </button>
    ) },
  ]
  return (
    <DataTable
      rows={runs}
      columns={colonne}
      rowKey={(r) => r.id}
      expanded={(r) => open === r.id}
      expand={(r) => <RunDetail run={r} />}
      conteggio={{ nome: t('esecuzioni') }}
    />
  )
}

/**
 * Il dettaglio di un'esecuzione: cosa è successo a ciascun oggetto e perché.
 *
 * La colonna che conta è il motivo. Un oggetto saltato senza motivo scritto è un
 * difetto che non si trova più: il conteggio dice «saltati: 340» e non c'è modo
 * di sapere se sono tutti lo stesso caso o trecento casi diversi.
 */
function RunDetail({ run }: { run: SyncRun }) {
  const colonne: Column<SyncEvent>[] = [
    { key: 'action', header: t('Azione') },
    { key: 'object', header: t('Oggetto'), value: (e) => `${e.source}${e.target ? ` → ${e.target}` : ''}` },
    { key: 'reason', header: t('Motivo') },
    { key: 'payload', header: t('Payload'), filter: false, sort: false, value: (e) => (e.payload ? JSON.stringify(e.payload) : ''), render: (e) => (
      e.payload ? <pre className="json">{JSON.stringify(e.payload, null, 2)}</pre> : ''
    ) },
  ]
  return (
    <div className="report">
      {run.error && <p className="banner banner--error">{run.error}</p>}
      {run.dry_run && (
        <p className="banner banner--info">
          {t('Prova a vuoto: sul sistema di destinazione non è stato scritto niente. I payload qui sotto sono quelli che sarebbero stati inviati.')}
        </p>
      )}
      {!run.events.length ? (
        <p className="text-muted">{t('Nessun evento da segnalare.')}</p>
      ) : (
        <DataTable rows={run.events} columns={colonne} conteggio={{ nome: t('eventi') }} />
      )}
      {/* Un elenco tagliato in silenzio si legge come «erano solo questi». */}
      {run.events_truncated > 0 && (
        <p className="text-muted">
          {t('Altri {n} eventi non sono mostrati: questo elenco è parziale.', { n: run.events_truncated })}
        </p>
      )}
    </div>
  )
}

